import { useState } from 'react';
import { Outlet } from 'react-router';
import { LogOut } from 'lucide-react';
import { useSession } from '@/contexts/session-context';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAdminFetch } from '@/hooks/use-admin-fetch';
import { AppShell } from '@/components/app-shell';
import { AppHeader } from '@/components/app-header';
import { HeaderButton } from '@/components/header-button';
import { SuperAdminSidebar } from '@/components/super-admin-sidebar';

const IS_PRODUCTION = import.meta.env.VITE_SENTRY_ENVIRONMENT === 'production';

/** Reset-DB action — staging/local only (hidden in production). */
function ResetDatabaseAction() {
  const [resetting, setResetting] = useState(false);
  const adminFetch = useAdminFetch();

  const handleReset = async () => {
    setResetting(true);
    try {
      const res = await adminFetch('/super-admin/reset-database', { method: 'POST' });
      if (res.ok) {
        toast.success('Database reset complete');
      } else {
        const body = await res.json().catch(() => ({ message: 'Unknown error' }));
        toast.error(`Reset failed: ${body.message ?? 'Unknown error'}`);
      }
    } catch (err) {
      toast.error(`Reset failed: ${(err as Error).message}`);
    } finally {
      setResetting(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger render={<Button variant="destructive" size="sm" disabled={resetting} />}>
        {resetting ? 'Resetting...' : 'Reset Database'}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reset Database</AlertDialogTitle>
          <AlertDialogDescription>
            This will delete ALL users, workspaces, channels, and clean up Stripe,
            GHL, and WorkOS. This action cannot be undone. Only works in staging
            and local environments.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={handleReset}
          >
            Yes, Reset Everything
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function SuperAdminLayout() {
  const { user, signOut } = useSession();

  const actions = [
    ...(IS_PRODUCTION ? [] : [<ResetDatabaseAction key="reset" />]),
    <span key="name" className="hidden text-sm text-muted-foreground md:inline">{user?.firstName}</span>,
    <HeaderButton
      key="logout"
      icon={<LogOut className="size-4" />}
      label="Log Out"
      onClick={() => { localStorage.removeItem('active_workspace'); signOut(); }}
    />,
  ];

  return (
    <AppShell
      sidebar={<SuperAdminSidebar />}
      header={
        <AppHeader
          left={<span className="truncate text-base font-semibold md:text-xl">Super Admin Panel</span>}
          actions={actions}
        />
      }
    >
      {/* super-admin pages are full-width (PageContainer variant="fluid" — Task 14). */}
      <Outlet />
    </AppShell>
  );
}
